import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean, useSetState } from 'react-use'
import { useQueryClient } from '@tanstack/react-query'

import { OrgMembersAPI } from '@makyoapp/makyo-sdk'
import { OrgMemberRole, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { isEmail, OrgCardAdapter, TeamAdapter } from '@makyoapp/makyo-common'
import { Plus16 } from '@makyoapp/makyo-icon/icon'
import {
  Button,
  Checkbox,
  FallbackAvatar,
  Field,
  JustifyVariant,
  Label,
  ModalGrid,
  SelectDropdown,
  SimpleInput,
} from '@makyoapp/makyo-ui'

import { useDebounceInput } from '@/shared/makyo-hooks'
import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { appConfig } from '@/configs'
import { useGetOrgTeams } from '@/resolvers/org/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { ORG_ROLE_OPTIONS, TEAM_ROLE_OPTIONS } from '@/shared/constants'
import { cleanUpString } from '@/shared/utils'

export interface InviteMemberValue {
  name: string
  email: string
  orgRole: OrgMemberRole
  teams: {
    team: TeamAdapter
    role: TeamMemberRole
  }[]
}

export interface InviteMemberModalProps {
  org: OrgCardAdapter
  onSuccess?: (done: () => void) => void
  withButton?: boolean
  show: boolean
  setShow: (next: boolean) => void
}

/**
 * rendering invite member modal
 */
export function InviteMemberModal({ withButton = true, show, setShow, ...props }: InviteMemberModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { currentOrg } = useCurrentOrgContext()
  const { userData } = useAuth()
  const printAPIError = useReqError()
  const { data: teamsData } = useGetOrgTeams()

  const [isLoading, setIsLoading] = useBoolean(false)

  const [value, setValue] = useSetState<Pick<InviteMemberValue, 'teams' | 'orgRole'>>({
    orgRole: OrgMemberRole.Member,
    teams: [],
  })

  const [errors, setErrors] = useSetState<{
    name?: string
    email?: string
    teams?: string
  }>({
    name: undefined,
    email: undefined,
    teams: undefined,
  })

  const [debName, cancelDebName, setDebName, name, setName] = useDebounceInput('')

  const [debEmail, cancelDebEmail, setDebEmail, email, setEmail] = useDebounceInput('')

  const handleHide = useCallback(() => {
    setShow(false)
    setIsLoading(false)
    cancelDebName()
    setDebName('')
    setName('')
    cancelDebEmail()
    setDebEmail('')
    setEmail('')
    setValue({
      teams: [],
      orgRole: OrgMemberRole.Member,
    })
  }, [setShow, setIsLoading, cancelDebName, setDebName, setName, cancelDebEmail, setDebEmail, setEmail, setValue])

  useEffect(() => {
    if (!show) {
      setErrors({
        name: undefined,
        email: undefined,
        teams: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    if (debName) {
      setErrors({
        name: !name ? 'Please enter name' : undefined,
      })
    }

    if (debEmail) {
      setErrors({
        email: !isEmail(debEmail.trim()) ? 'Invalid email address' : undefined,
      })
    }
  }, [setErrors, debName, name, debEmail, email])

  const { org, onSuccess } = props
  const teams = useMemo(() => teamsData || [], [teamsData])
  const filterTeams = useMemo(() => {
    return teams.filter((ot) => {
      return ot.members.find((tm) => tm.userId === userData?.id) !== undefined
    })
  }, [teams, userData])

  const handleSubmit = useCallback(() => {
    setIsLoading(true)
    const member = {
      name: name.trim(),
      email: email.trim(),
      ...value,
    }

    OrgMembersAPI.ReqAdd(org.id, {
      _Name: member.name,
      _Email: member.email,
      // TODO: remove ignore when sdk update
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      _Teams: member.teams.map((mt) => {
        return {
          _TeamID: mt.team.id,
          _Role: mt.role,
        }
      }),
      _Role: member.orgRole,
    })
      .then(async () => {
        // revalidate members
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.orgMembers, currentOrg?.id],
        })

        if (onSuccess) {
          onSuccess(handleHide)
        } else {
          handleHide()
        }
        toasted(t('Member invitation sent!'))
      })
      .catch((e) => {
        printAPIError(e)
        handleHide()
      })
  }, [setIsLoading, name, email, value, org.id, queryClient, currentOrg?.id, onSuccess, handleHide, t, printAPIError])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (!!name) {
      timeout = setTimeout(() => {
        setName(cleanUpString(name))
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [name, setName])

  useEffect(() => {
    if (debEmail && isEmail(debEmail)) {
      OrgMembersAPI.ReqCheckMember(org.id, { _Email: debEmail })
        .then((response) => {
          if (response) {
            setErrors({ email: 'The user already member of the organization' })
          } else {
            setErrors({ email: '' })
          }
        })
        .catch((error) => {
          printAPIError(error)
        })
    }
  }, [debEmail, setErrors, printAPIError])

  const nameCharCount = name.trim().length
  const validNameLength =
    nameCharCount >= appConfig.charConstraints.min && nameCharCount < appConfig.charConstraints.max

  return (
    <>
      {withButton && (
        <Button id='invite-member-btn' className='text-m-m' onClick={() => setShow(true)} icon={<Plus16 />}>
          {t('Invite new member')}
        </Button>
      )}
      <ModalGrid
        id='invite-member-modal'
        show={show}
        onHide={handleHide}
        title={t('Invite new member')}
        submitId='invite-member-submit'
        isLoading={isLoading}
        disabled={[!validNameLength, Object.values(errors).some((v) => !!v), !name.trim(), !isEmail(email.trim())].some(
          (v) => v
        )}
        primaryTrailing={t('Send Invite')}
        onConfirm={handleSubmit}
        onCancel={handleHide}
      >
        <Field>
          <Label required defaultSize={false} className='lbl'>
            {t('Name')}
          </Label>
          <SimpleInput
            id='invite-member-name'
            required
            value={name}
            error={errors.name && t(errors.name)}
            onChange={(e) => {
              setName(e.currentTarget.value)
            }}
            type='text'
            withCounterConfig={appConfig.charConstraints}
          />
        </Field>
        <Field>
          <Label required defaultSize={false} className='lbl'>
            {t('Email')}
          </Label>
          <SimpleInput
            id='invite-member-email'
            className='rtl:[direction:ltr]'
            required
            value={email}
            error={errors.email && t(errors.email)}
            onChange={(e) => {
              setEmail(e.currentTarget.value.trim())
            }}
          />
        </Field>
        <div className='text-light text-m-sm uppercase pt-8 pb-2'>{t('Organization Access Level')}</div>
        <Field>
          <Label defaultSize={false} className='lbl'>
            <div className='flex-c'>
              <FallbackAvatar text={org.name} alt={org.name} src={org.avatar} />
              <div>{org.name}</div>
            </div>
          </Label>
          <SelectDropdown
            id='invite-member-org-role'
            value={value.orgRole}
            onChange={(orgRole) => {
              setValue({
                orgRole,
              })
            }}
            options={ORG_ROLE_OPTIONS}
            renderOption={(opt) => {
              return t(opt)
            }}
          />
        </Field>
        {filterTeams.length > 0 && (
          <div className='text-light text-m-sm uppercase pt-8 pb-2'>{t('Add to Team & Set Team Access Level')}</div>
        )}
        {filterTeams.map((team, idx) => {
          return (
            <Field key={team.id}>
              <Checkbox
                id={`invite-member-team-${team.id}`}
                className='max-w-[200px]'
                labelClassName={'gap-4!'}
                justify={JustifyVariant.Start}
                checked={value.teams.filter((t) => t.team.id === team.id).length > 0}
                label={
                  <div className='flex-c'>
                    <FallbackAvatar text={team.name} alt={team.name} src={team.avatar} />
                    <div>{team.name}</div>
                  </div>
                }
                onClick={(isChecked) => {
                  if (isChecked) {
                    setValue({
                      teams: [...value.teams, { team, role: TeamMemberRole.Viewer }],
                    })
                  } else {
                    setValue({
                      teams: value.teams.filter((t) => t.team.id !== team.id),
                    })
                  }
                }}
              />
              <SelectDropdown
                id={`invite-member-team-${team.id}-role`}
                disabled={value.teams.filter((te) => te.team.id === team.id).length < 1}
                value={value.teams[idx]?.role || TeamMemberRole.Viewer}
                onChange={(opt) => {
                  const newVal = [...value.teams]
                  const idx = newVal.findIndex((t) => t.team.id === team.id)
                  if (idx !== -1) {
                    newVal[idx].role = opt
                    setValue({
                      teams: newVal,
                    })
                  }
                }}
                options={TEAM_ROLE_OPTIONS}
              />
            </Field>
          )
        })}
      </ModalGrid>
    </>
  )
}
